import { PlusIcon } from "@radix-ui/react-icons";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { MouseEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Notifications from "util/notifications";
import { HeaderActions } from "./AutomationsPage";
import TurnOnDialog from "./TurnOnDialog";
import AutomationsTabEditor, { EditorTarget } from "./AutomationsTabEditor";
import {
  insertAfter,
  laneColumns,
  makeStep,
  nameAfter,
  nextStage,
  nextStep,
  orderSteps,
  retimed,
  timeAfter,
  withNext,
} from "./AutomationsTabModel";
import { Lane, LaneActions } from "./AutomationsTabTimeline";
import {
  LoadingRows,
  useAutomations,
  useAutomationsApi,
  useReference,
} from "./hooks";
import { automationProblems, clone, draftOf, RIGHT_AWAY, uid } from "./model";
import type { Automation, AutomationDraft, Gate, Step } from "./types";
import { plural } from "./words";

/** An automation as a draft to edit, its steps in the order the lane shows them. */
function draftFrom(automation: Automation): AutomationDraft {
  const draft = draftOf(automation);
  return { ...draft, steps: orderSteps(draft.steps) };
}

/** The lanes in the page, by automation id. */
function laneOf(id: string) {
  return (
    Array.from(
      document.querySelectorAll<HTMLElement>("[data-automation-id]")
    ).find((el) => el.getAttribute("data-automation-id") === id) ?? null
  );
}

/**
 * The Automations tab: every automation as its own row, by the kind of
 * trigger that starts it (the kinds' order), then by name. A row is its
 * name's pill (Paused while off), then its timeline, left to right: its
 * trigger cards, then its steps and gates; steps at the same moment stack
 * in one column. A card or Add opens the step dialog. A row stays where it
 * is when it is paused or turned on.
 *
 * ?open=<automation id> brings that row into view, highlighted;
 * ?template=<template id> highlights the automations that send that
 * template.
 */
export default function AutomationsTab() {
  const ref = useReference();
  const api = useAutomationsApi();
  const { automations, loading, error } = useAutomations();
  const [params, setParams] = useSearchParams();
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [flash, setFlash] = useState<{ ids: string[]; seq: number } | null>(
    null
  );
  const [turningOff, setTurningOff] = useState<Automation | null>(null);
  const [turningOn, setTurningOn] = useState<Automation | null>(null);
  const [deleting, setDeleting] = useState<Automation | null>(null);
  const [busy, setBusy] = useState(false);

  // A link's ?open= or ?template=: bring into view and highlight.
  const openParam = params.get("open");
  const templateParam = params.get("template");
  useEffect(() => {
    if (loading || (!openParam && !templateParam)) return;
    const targets = openParam
      ? automations.filter((a) => a.id === openParam)
      : automations.filter((a) =>
          a.steps.some(
            (s) =>
              s.action === "message" &&
              String(s.message.templateId) === templateParam
          )
        );
    if (targets.length)
      setFlash({ ids: targets.map((a) => a.id), seq: Date.now() });
    // Used: the same link works again later.
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("open");
        next.delete("template");
        return next;
      },
      { replace: true }
    );
  }, [loading, openParam, templateParam, automations, setParams]);

  useEffect(() => {
    if (!flash) return;
    laneOf(flash.ids[0])?.scrollIntoView?.({
      block: "center",
      behavior: "smooth",
    });
    const timer = setTimeout(
      () => setFlash((f) => (f?.seq === flash.seq ? null : f)),
      2600
    );
    return () => clearTimeout(timer);
  }, [flash]);

  if (loading) return <LoadingRows />;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  /** Opens the step dialog. */
  const open = (next: Omit<EditorTarget, "id">) =>
    setEditor({ ...next, id: uid("editor") });

  const close = () => setEditor(null);

  const saved = (automationId: string | null) => {
    setEditor(null);
    if (automationId) setFlash({ ids: [automationId], seq: Date.now() });
  };

  /** New automation, from the header's button: its trigger first. */
  const openNew = (anchor: HTMLElement) =>
    open({
      mode: "new",
      automationId: null,
      draft: null,
      stepId: null,
      when: false,
      anchor,
      title: "New automation",
    });

  /** A new step in a copy of the automation. when: Add next step's When. */
  const openAdd = (
    automation: Automation,
    draft: AutomationDraft,
    step: Step,
    anchor: HTMLElement,
    when = false
  ) =>
    open({
      mode: "add",
      automationId: automation.id,
      draft,
      stepId: step.id,
      when,
      anchor,
      title: "Add a step",
    });

  /** Saves a change made on the lane, or says why it can't be. */
  const saveLane = (
    automation: Automation,
    next: { steps: Step[]; gates: Gate[] }
  ) => {
    const lane = { ...next, trigger: automation.trigger };
    const problem = automationProblems(ref, lane)[0];
    if (problem) {
      Notifications.error(problem.text);
      return;
    }
    api
      .updateAutomation(automation.id, {
        ...next,
        name: nameAfter(ref, automation, lane),
      })
      .catch((e) =>
        Notifications.error(errorText(e, "That could not be changed."))
      );
  };

  /** A copy of `step`, right after it: the same moment, so the same column. */
  const copyOf = (automation: Automation, step: Step) => {
    const draft = draftFrom(automation);
    const copy: Step = { ...clone(step), id: uid("step") };
    const at = draft.steps.findIndex((s) => s.id === step.id);
    return {
      draft: {
        ...draft,
        steps: orderSteps([
          ...draft.steps.slice(0, at + 1),
          copy,
          ...draft.steps.slice(at + 1),
        ]),
      },
      copy,
    };
  };

  const setOn = (automation: Automation, on: boolean) =>
    api
      .toggleAutomation(automation.id, on)
      .then(() =>
        Notifications.success(
          on ? `${automation.name} is on.` : `${automation.name} is paused.`
        )
      )
      .catch((e) =>
        Notifications.error(errorText(e, "That could not be changed."))
      );

  // What turning it off drops: what is scheduled and waiting for approval
  // (people at a wait carry on when it is turned back on).
  const drops = (automation: Automation) =>
    automation.stats.scheduled + automation.stats.waiting;

  const actions: LaneActions = {
    // On: who it reaches first. Off: a confirm when it drops something.
    toggle: (automation, on) => {
      if (on) setTurningOn(automation);
      else if (drops(automation) > 0) setTurningOff(automation);
      else setOn(automation, false);
    },
    openStep: (automation, step, anchor) =>
      open({
        mode: "edit",
        automationId: automation.id,
        draft: draftFrom(automation),
        stepId: step.id,
        when: false,
        anchor,
        title: "Edit this step",
      }),
    addToColumn: (automation, column, anchor) => {
      const draft = draftFrom(automation);
      const step = makeStep(ref, draft, "task", column.position);
      openAdd(
        automation,
        { ...draft, steps: insertAfter(draft.steps, column, step) },
        step,
        anchor
      );
    },
    // The dialog's When starts at the next usual time after the last column.
    addNext: (automation, anchor) => {
      const draft = draftFrom(automation);
      const gates = draft.gates ?? [];
      const columns = laneColumns(draft.steps, gates);
      const last = columns[columns.length - 1];
      const step = nextStep(
        ref,
        draft,
        timeAfter(ref, draft, columns, columns.length - 1),
        last?.gate ?? null
      );
      openAdd(
        automation,
        { ...draft, steps: withNext(draft.steps, step, gates) },
        step,
        anchor,
        true
      );
    },
    // A gate after the last column, its kind picked first, and the step
    // that happens right as it passes.
    addGate: (automation, anchor) => {
      const draft = draftFrom(automation);
      const gates = draft.gates ?? [];
      const gate: Gate = {
        id: uid("gate"),
        stage: nextStage(draft.steps, gates),
        logic: "and",
        cards: [],
      };
      const step: Step = {
        ...makeStep(ref, draft, "task", RIGHT_AWAY),
        stage: gate.stage,
      };
      open({
        mode: "gate",
        automationId: automation.id,
        draft: {
          ...draft,
          gates: [...gates, gate],
          steps: [...draft.steps, step],
        },
        stepId: step.id,
        gateId: gate.id,
        when: false,
        anchor,
        title: "Add a trigger gate",
      });
    },
    rename: (automation, name) =>
      api
        .updateAutomation(automation.id, { name })
        .catch((e) =>
          Notifications.error(errorText(e, "That name could not be saved."))
        ),
    retime: (automation, column, position) => {
      const draft = draftFrom(automation);
      const next = retimed(
        { steps: draft.steps, gates: draft.gates ?? [] },
        column,
        clone(position)
      );
      saveLane(automation, { ...next, steps: orderSteps(next.steps) });
    },
    remove: (automation) => setDeleting(automation),
  };

  /** Deletes it: what it already did stays in Activity, what it had not done is cleared. */
  const remove = (automation: Automation) => {
    setBusy(true);
    api
      .deleteAutomation(automation.id)
      .then(() => {
        Notifications.success(`${automation.name} was deleted.`);
        setDeleting(null);
      })
      .catch((e) =>
        Notifications.error(errorText(e, "It could not be deleted."))
      )
      .finally(() => setBusy(false));
  };

  /** The editor's Duplicate: the step as it is there, as a new step after it. */
  const duplicateFromEditor = (automationId: string, step: Step) => {
    const automation = automations.find((a) => a.id === automationId);
    if (!automation || !editor) return;
    const { draft, copy } = copyOf(automation, step);
    setEditor({
      id: uid("editor"),
      mode: "add",
      automationId,
      draft,
      stepId: copy.id,
      when: false,
      anchor: editor.anchor,
      title: "Add a step",
    });
  };

  const pending = turningOff ? drops(turningOff) : 0;

  return (
    <>
      <HeaderActions>
        <Button
          color={ButtonColor.GREEN}
          size={ButtonSize.SMALL}
          onClick={(e: MouseEvent<HTMLElement>) => openNew(e.currentTarget)}
        >
          <PlusIcon /> New automation
        </Button>
      </HeaderActions>

      {ref.problems.length > 0 && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {ref.problems.join(" ")}
        </p>
      )}

      {!automations.length && (
        <p className="py-6 text-center text-sm text-gray-500">
          No automations yet.
        </p>
      )}

      {automations.length > 0 && (
        <div className="space-y-4">
          {automations.map((automation) => (
            <Lane
              key={automation.id}
              automation={automation}
              editingStepId={
                editor?.mode === "edit" && editor.automationId === automation.id
                  ? editor.stepId
                  : null
              }
              flash={!!flash?.ids.includes(automation.id)}
              actions={actions}
            />
          ))}
        </div>
      )}

      {editor && (
        <AutomationsTabEditor
          key={editor.id}
          target={editor}
          onClose={close}
          onSaved={saved}
          onDuplicate={duplicateFromEditor}
        />
      )}

      {turningOn && (
        <TurnOnDialog
          name={turningOn.name}
          target={{ id: turningOn.id }}
          onTurnOn={(turnOn) =>
            api.toggleAutomation(turningOn.id, true, turnOn).then(() => {
              Notifications.success(`${turningOn.name} is on.`);
              setTurningOn(null);
            })
          }
          onClose={() => setTurningOn(null)}
        />
      )}

      <Dialog
        open={turningOff !== null}
        onOpenChange={(next) => !next && setTurningOff(null)}
      >
        {turningOff && (
          <DialogContent className="dialog-content">
            <h2 className="mb-2 text-xl font-semibold">
              Pause {turningOff.name}?
            </h2>
            <p className="text-sm text-gray-700">
              {`${plural(pending, "step")} ${
                pending === 1 ? "has" : "have"
              } not happened yet. Pausing drops ${
                pending === 1 ? "it" : "them"
              }.`}
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                onClick={() => {
                  setOn(turningOff, false);
                  setTurningOff(null);
                }}
              >
                Pause
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
      >
        {deleting && (
          <DialogContent className="dialog-content">
            <h2 className="mb-2 text-xl font-semibold">
              Delete {deleting.name}?
            </h2>
            <p className="text-sm text-gray-700">
              What it already did stays in Activity.
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                disabled={busy}
                onClick={() => remove(deleting)}
              >
                Delete
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
